import type { AnalyticsConsentChoice } from "./consent-store"

export const GOOGLE_ANALYTICS_ID = "G-2X1YWGET59"
export const GOOGLE_ANALYTICS_SCRIPT_ID = "byfolio-google-analytics"

type GtagArguments = IArguments

interface AnalyticsWindow extends Window {
  dataLayer?: GtagArguments[]
  gtag?: (...args: unknown[]) => void
  [key: `ga-disable-${string}`]: boolean | undefined
}

interface RouteLocation {
  readonly pathname: string
  readonly search?: string
  readonly href?: string
}

export interface BrowserEnvironment {
  readonly window: AnalyticsWindow
  readonly document: Document
  readonly requestAnimationFrame: (callback: FrameRequestCallback) => number
}

const deniedConsent = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
} as const

const rootDomain = (hostname: string): string | null => {
  const labels = hostname.split(".").filter(Boolean)
  return labels.length > 1 ? labels.slice(-2).join(".") : null
}

export class GoogleAnalyticsController {
  private initialized = false
  private choice: AnalyticsConsentChoice = "unset"
  private configured = false
  private scriptInjected = false
  private routeSequence = 0
  private latestCompletedRoute?: {
    readonly sequence: number
    readonly path: string
    readonly location: string
    readonly title: string
  }
  private lastSentSequence: number | undefined

  constructor(
    private readonly enabled: boolean,
    private readonly environment?: BrowserEnvironment
  ) {}

  initialize(): void {
    if (!this.enabled || !this.environment || this.initialized) return

    const browserWindow = this.environment.window
    browserWindow.dataLayer ??= []
    browserWindow.gtag ??= function gtag() {
      // Google documents this queue entry as the function's arguments object.
      // eslint-disable-next-line prefer-rest-params
      browserWindow.dataLayer?.push(arguments)
    }
    browserWindow.gtag("consent", "default", deniedConsent)
    browserWindow[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = true
    this.initialized = true
  }

  applyConsent(choice: AnalyticsConsentChoice): void {
    this.choice = choice
    if (!this.enabled || !this.environment) return

    this.initialize()
    const browserWindow = this.environment.window

    if (choice !== "accepted") {
      this.lastSentSequence = undefined
      browserWindow[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = true
      browserWindow.gtag?.("consent", "update", deniedConsent)
      this.deleteAnalyticsCookies()
      return
    }

    browserWindow[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = false
    browserWindow.gtag?.("consent", "update", {
      ...deniedConsent,
      analytics_storage: "granted",
    })
    this.configureAndLoad()
    this.sendLatestRoute()
  }

  recordRoute(location: RouteLocation): void {
    if (!this.enabled || !this.environment) return
    this.initialize()

    const sequence = ++this.routeSequence
    const path = `${location.pathname}${location.search ?? ""}`
    const absoluteLocation =
      location.href ?? new URL(path, this.environment.window.location.href).href

    this.environment.requestAnimationFrame(() => {
      this.environment?.requestAnimationFrame(() => {
        if (sequence !== this.routeSequence || !this.environment) return

        this.latestCompletedRoute = {
          sequence,
          path,
          location: absoluteLocation,
          title: this.environment.document.title,
        }
        this.sendLatestRoute()
      })
    })
  }

  private configureAndLoad(): void {
    if (!this.environment) return
    const { document: browserDocument, window: browserWindow } =
      this.environment

    if (!this.configured) {
      browserWindow.gtag?.("js", new Date())
      browserWindow.gtag?.("config", GOOGLE_ANALYTICS_ID, {
        anonymize_ip: true,
        send_page_view: false,
      })
      this.configured = true
    }

    if (
      !this.scriptInjected &&
      !browserDocument.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)
    ) {
      const script = browserDocument.createElement("script")
      script.id = GOOGLE_ANALYTICS_SCRIPT_ID
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`
      browserDocument.head.append(script)
    }
    this.scriptInjected = true
  }

  private sendLatestRoute(): void {
    if (
      this.choice !== "accepted" ||
      !this.environment ||
      !this.latestCompletedRoute ||
      this.lastSentSequence === this.latestCompletedRoute.sequence
    ) {
      return
    }

    const route = this.latestCompletedRoute
    this.environment.window.gtag?.("event", "page_view", {
      page_path: route.path,
      page_location: route.location,
      page_title: route.title,
    })
    this.lastSentSequence = route.sequence
  }

  private deleteAnalyticsCookies(): void {
    if (!this.environment) return

    const browserDocument = this.environment.document
    const hostname = this.environment.window.location.hostname
    const domains = new Set<string | null>([
      null,
      hostname || null,
      rootDomain(hostname),
    ])
    const names = browserDocument.cookie
      .split(";")
      .map(cookie => cookie.trim().split("=", 1)[0])
      .filter((name): name is string => name !== undefined)
      .filter(name => name === "_ga" || name.startsWith("_ga_"))

    names.forEach(name => {
      domains.forEach(domain => {
        const domainAttribute = domain ? `; Domain=${domain}` : ""
        browserDocument.cookie = `${name}=; Max-Age=0; Path=/${domainAttribute}; SameSite=Lax`
      })
    })
  }
}

export const createBrowserAnalyticsEnvironment = (): BrowserEnvironment => ({
  window: window as unknown as AnalyticsWindow,
  document,
  requestAnimationFrame: callback => window.requestAnimationFrame(callback),
})
